import { Eye, RefreshCw, Search, Sparkles } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { GroupedItemDetailDto, GroupedItemDto, GroupedItemListing, StoreDto } from "../../shared/types";
import { ClassifyModal } from "../components/ClassifyModal";
import { FindMatchesModal } from "../components/FindMatchesModal";
import { ItemTile, type VariantRowData } from "../components/ItemTile";
import { Button, EmptyState, ErrorState, InfoTip, Page } from "../ui";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
// v2: GroupedItemDto gained productId, which every tile links with.
const CACHE_KEY = "items.groups.cache.v2";
const CACHE_TTL_MS = 10 * 60 * 1000;

type ItemsCache = {
	storeId: string;
	q: string;
	inStockOnly: boolean;
	watchedOnly: boolean;
	items: GroupedItemDto[];
	nextBefore: number | null;
	total: number;
	scrollY: number;
	savedAt: number;
};

async function fetchListing(qs: URLSearchParams): Promise<GroupedItemListing> {
	const res = await fetch(`/api/items?${qs}`);
	return (await res.json()) as GroupedItemListing;
}

async function fetchStores(): Promise<StoreDto[]> {
	const res = await fetch("/api/stores");
	const data = (await res.json()) as { stores: StoreDto[] };
	return data.stores;
}

function buildQs(opts: {
	storeId: string;
	q: string;
	inStockOnly: boolean;
	watchedOnly: boolean;
	before: number | null;
}): URLSearchParams {
	const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
	if (opts.storeId) qs.set("storeId", opts.storeId);
	if (opts.q.trim()) qs.set("q", opts.q.trim());
	if (opts.inStockOnly) qs.set("inStock", "true");
	if (opts.watchedOnly) qs.set("watched", "true");
	if (opts.before != null) qs.set("before", String(opts.before));
	return qs;
}

function readCache(): ItemsCache | null {
	try {
		const raw = sessionStorage.getItem(CACHE_KEY);
		if (!raw) return null;
		const c = JSON.parse(raw) as ItemsCache;
		if (Date.now() - c.savedAt > CACHE_TTL_MS) return null;
		return c;
	} catch {
		return null;
	}
}

export function Items() {
	const cached = useRef<ItemsCache | null>(typeof window === "undefined" ? null : readCache());

	const [stores, setStores] = useState<StoreDto[]>([]);
	const [storeId, setStoreId] = useState(cached.current?.storeId ?? "");
	const [q, setQ] = useState(cached.current?.q ?? "");
	const [debouncedQ, setDebouncedQ] = useState(cached.current?.q ?? "");
	const [inStockOnly, setInStockOnly] = useState(cached.current?.inStockOnly ?? false);
	const [watchedOnly, setWatchedOnly] = useState(cached.current?.watchedOnly ?? false);
	const [items, setItems] = useState<GroupedItemDto[]>(cached.current?.items ?? []);
	const [nextBefore, setNextBefore] = useState<number | null>(cached.current?.nextBefore ?? null);
	const [total, setTotal] = useState<number>(cached.current?.total ?? 0);
	const [loading, setLoading] = useState(cached.current ? false : true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const reqIdRef = useRef(0);
	const hydratedRef = useRef(cached.current != null);
	const scrollYRef = useRef(cached.current?.scrollY ?? 0);

	// Per-store variants, keyed by groupKey — /api/items carries none, they
	// only arrive from /api/items/group/:groupKey. Fetched for in-stock groups
	// only (see the effect below); dropped wholesale by Refresh.
	const [variantCache, setVariantCache] = useState<Map<string, VariantRowData[]>>(() => new Map());
	// Every live group on the page asks at once, and the effect re-runs on
	// each cache write, so requests already out are tracked here.
	const inFlight = useRef<Set<string>>(new Set());
	const sentinel = useRef<HTMLDivElement | null>(null);
	// Groups whose /api/items/group/:groupKey fetch failed, keyed by groupKey.
	// Without this, a failed fetch leaves `variants` permanently null and the
	// tile renders skeleton placeholders forever with nothing telling the
	// operator anything went wrong. An entry also stops the effect below from
	// re-requesting that group in a loop; clearing it (Retry, Refresh) is what
	// lets it try again.
	const [expandErrors, setExpandErrors] = useState<Map<string, string>>(() => new Map());

	useEffect(() => {
		fetchStores()
			.then(setStores)
			.catch(() => {});
	}, []);

	const [findOpen, setFindOpen] = useState(false);
	const [classifying, setClassifying] = useState<{ groupKey: string; title: string } | null>(null);

	const storeNames = useMemo(() => new Map(stores.map((s) => [s.id, s.name])), [stores]);

	useEffect(() => {
		const c = cached.current;
		if (!c) return;
		cached.current = null;
		requestAnimationFrame(() => {
			window.scrollTo(0, c.scrollY);
		});
	}, []);

	// The top bar's search hands off as #/items?q=… — take the query, then drop
	// it from the address so it doesn't re-apply over later typing on reload.
	useEffect(() => {
		const take = () => {
			const raw = window.location.hash;
			const i = raw.indexOf("?");
			if (i === -1) return;
			const incoming = new URLSearchParams(raw.slice(i + 1)).get("q");
			if (incoming == null) return;
			hydratedRef.current = false;
			setQ(incoming);
			setDebouncedQ(incoming);
			window.history.replaceState(null, "", "#/items");
		};
		take();
		window.addEventListener("hashchange", take);
		return () => window.removeEventListener("hashchange", take);
	}, []);

	useEffect(() => {
		const t = setTimeout(() => setDebouncedQ(q), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(t);
	}, [q]);

	const load = useCallback(async () => {
		const reqId = ++reqIdRef.current;
		setLoading(true);
		setError(null);
		try {
			const data = await fetchListing(
				buildQs({
					storeId,
					q: debouncedQ,
					inStockOnly,
					watchedOnly,
					before: null,
				}),
			);
			if (reqId !== reqIdRef.current) return;
			setItems(data.items);
			setNextBefore(data.nextBefore);
			setTotal(data.total ?? 0);
		} catch (e) {
			if (reqId !== reqIdRef.current) return;
			setError((e as Error).message ?? "failed to load");
		} finally {
			if (reqId === reqIdRef.current) setLoading(false);
		}
	}, [storeId, debouncedQ, inStockOnly, watchedOnly]);

	useEffect(() => {
		if (hydratedRef.current) {
			hydratedRef.current = false;
			return;
		}
		void load();
	}, [load]);

	const loadMore = useCallback(async () => {
		if (nextBefore == null || loadingMore) return;
		setLoadingMore(true);
		try {
			const data = await fetchListing(
				buildQs({
					storeId,
					q: debouncedQ,
					inStockOnly,
					watchedOnly,
					before: nextBefore,
				}),
			);
			setItems((prev) => [...prev, ...data.items]);
			setNextBefore(data.nextBefore);
			setTotal(data.total ?? 0);
		} finally {
			setLoadingMore(false);
		}
	}, [nextBefore, loadingMore, storeId, debouncedQ, inStockOnly, watchedOnly]);

	const hasMore = nextBefore != null;

	// Sentinel at the end of the rendered list; when it enters the viewport,
	// load the next page. Keeps the mounted row count bounded by what has been
	// paged in, rather than by the ~24,000-row catalogue.
	useEffect(() => {
		const el = sentinel.current;
		if (!el || !hasMore) return;
		const io = new IntersectionObserver((entries) => {
			if (entries.some((e) => e.isIntersecting)) void loadMore();
		});
		io.observe(el);
		return () => io.disconnect();
	}, [hasMore, loadMore]);

	const fetchVariants = useCallback(async (groupKey: string) => {
		if (inFlight.current.has(groupKey)) return;
		inFlight.current.add(groupKey);
		try {
			const res = await fetch(`/api/items/group/${encodeURIComponent(groupKey)}`);
			if (!res.ok) {
				const body = (await res.json().catch(() => ({}))) as {
					error?: string;
				};
				throw new Error(body.error ?? `HTTP ${res.status}`);
			}
			const data = (await res.json()) as GroupedItemDetailDto;
			setVariantCache((prev) =>
				new Map(prev).set(
					groupKey,
					data.variants.map((v) => ({
						productId: v.id,
						storeId: v.storeId,
						title: v.title,
						priceMinor: v.priceMinor,
						inStock: v.inStock,
					})),
				),
			);
			setExpandErrors((prev) => {
				if (!prev.has(groupKey)) return prev;
				const next = new Map(prev);
				next.delete(groupKey);
				return next;
			});
		} catch (e) {
			// Lead with a stable sentence naming the failure; the raw reason is
			// secondary detail in parentheses, not the whole message — a bare
			// `TypeError` string on its own doesn't say what happened.
			const reason = (e as Error).message || "request failed";
			setExpandErrors((prev) => new Map(prev).set(groupKey, `Couldn't load store listings for this item (${reason}).`));
		} finally {
			inFlight.current.delete(groupKey);
		}
	}, []);

	// In-stock groups get their store lines fetched as soon as they're on the
	// page, so no tap sits between a restock and the listing you'd buy from.
	// A single-store group has no store lines (see ItemTile), so no fetch.
	useEffect(() => {
		for (const it of items) {
			if (
				it.inStockCount > 0 &&
				it.storeCount > 1 &&
				!variantCache.has(it.groupKey) &&
				!expandErrors.has(it.groupKey)
			) {
				void fetchVariants(it.groupKey);
			}
		}
	}, [items, variantCache, expandErrors, fetchVariants]);

	// Clearing the error is the whole retry: the effect above picks it up.
	const retryVariants = useCallback((groupKey: string) => {
		setExpandErrors((prev) => {
			if (!prev.has(groupKey)) return prev;
			const next = new Map(prev);
			next.delete(groupKey);
			return next;
		});
	}, []);

	// Refresh means fresh store lines too, not just a fresh listing.
	const refresh = useCallback(() => {
		setVariantCache(new Map());
		setExpandErrors(new Map());
		void load();
	}, [load]);

	useEffect(() => {
		let ticking = false;
		const onScroll = () => {
			if (!ticking) {
				ticking = true;
				requestAnimationFrame(() => {
					scrollYRef.current = window.scrollY;
					ticking = false;
				});
			}
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	useEffect(() => {
		const writeCache = () => {
			try {
				const snap: ItemsCache = {
					storeId,
					q,
					inStockOnly,
					watchedOnly,
					items,
					nextBefore,
					total,
					scrollY: scrollYRef.current,
					savedAt: Date.now(),
				};
				sessionStorage.setItem(CACHE_KEY, JSON.stringify(snap));
			} catch {}
		};
		window.addEventListener("beforeunload", writeCache);
		return () => {
			window.removeEventListener("beforeunload", writeCache);
			writeCache();
		};
	}, [storeId, q, inStockOnly, watchedOnly, items, nextBefore, total]);

	const showCount = !loading || items.length > 0;
	const now = Date.now();

	const filtersActive = !!(storeId || q || inStockOnly || watchedOnly);

	return (
		<Page
			title="Items"
			help={
				<InfoTip label="About items">
					Grouped by EAN across stores. Each price is the cheapest in stock, or what it last sold for. Find matches uses
					AI to find the items shown at other stores, and lets you merge them.
				</InfoTip>
			}
			count={showCount ? `${total.toLocaleString("nb-NO")} grouped by EAN` : undefined}
			actions={
				<>
					<Button
						variant="secondary"
						size="sm"
						onClick={() => setFindOpen(true)}
						disabled={items.length === 0}
						title="Use AI to find these items at other stores and merge them"
					>
						<Sparkles className="size-3.5" aria-hidden />
						Find matches
					</Button>
					<Button variant="ghost" size="sm" onClick={refresh} disabled={loading}>
						<RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} aria-hidden />
						Refresh
					</Button>
				</>
			}
		>
			{/* Sticky under the top bar so filters stay one reach away down a long
          grid. The ground-coloured backing hides tiles scrolling beneath. */}
			<div className="sticky top-12 z-20 -mx-3 mb-2 bg-ground/90 px-3 py-2 backdrop-blur-md sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6">
				<div className="flex flex-wrap items-center gap-1.5">
					<label className="relative min-w-56 flex-1">
						<Search
							className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted"
							aria-hidden
						/>
						<input
							type="search"
							placeholder="Search by title or EAN"
							value={q}
							onChange={(e) => setQ(e.target.value)}
							className="h-9 w-full rounded-lg border border-hairline bg-paper pr-3 pl-8 text-[13px] text-ink outline-none placeholder:text-muted focus:border-line sm:h-8"
						/>
					</label>
					<select
						value={storeId}
						onChange={(e) => setStoreId(e.target.value)}
						className="h-9 rounded-lg border border-hairline bg-paper px-2 text-[13px] text-ink hover:border-line sm:h-8"
						title="Show items that have at least one variant in the selected store"
						aria-label="Store"
					>
						<option value="">All stores</option>
						{stores.map((s) => (
							<option key={s.id} value={s.id}>
								{s.name}
							</option>
						))}
					</select>
					<Toggle checked={inStockOnly} onChange={setInStockOnly}>
						In stock
					</Toggle>
					<Toggle checked={watchedOnly} onChange={setWatchedOnly}>
						<Eye className="size-3.5" aria-hidden />
						Watched
					</Toggle>
					<span className="ml-auto pl-2 text-xs text-muted">
						{loading && items.length === 0
							? "Loading…"
							: `${items.length.toLocaleString("nb-NO")} of ${total.toLocaleString("nb-NO")} shown`}
					</span>
				</div>
			</div>

			{error && (
				<div className="mb-2">
					<ErrorState message={error} onRetry={() => void load()} />
				</div>
			)}

			{!loading && items.length === 0 && !error && (
				<EmptyState
					icon={Search}
					title="No items match these filters."
					description={filtersActive ? "Clear a filter to see more." : undefined}
				/>
			)}

			{items.length > 0 && (
				<ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-[repeat(auto-fill,minmax(152px,1fr))] sm:gap-2">
					{items.map((it) => (
						<li key={it.groupKey} className="grid">
							<ItemTile
								group={it}
								variants={variantCache.get(it.groupKey) ?? null}
								storeNames={storeNames}
								now={now}
								error={expandErrors.get(it.groupKey) ?? null}
								onRetry={() => retryVariants(it.groupKey)}
								onClassify={() => setClassifying({ groupKey: it.groupKey, title: it.title })}
							/>
						</li>
					))}
				</ul>
			)}

			<ClassifyModal group={classifying} storeNames={storeNames} onClose={() => setClassifying(null)} />

			<FindMatchesModal
				open={findOpen}
				onClose={() => setFindOpen(false)}
				groupKeys={items.map((it) => it.groupKey)}
				storeNames={storeNames}
				onMerged={refresh}
			/>

			{hasMore && items.length > 0 && (
				<div ref={sentinel} className="mt-3 flex justify-center py-4 text-xs text-muted">
					{loadingMore ? "Loading more…" : ""}
				</div>
			)}
		</Page>
	);
}

/** A filter that is on or off, as a pill. Checkbox underneath so it stays a
 *  form control for keyboards and screen readers. */
function Toggle({
	checked,
	onChange,
	children,
}: {
	checked: boolean;
	onChange: (v: boolean) => void;
	children: ReactNode;
}) {
	return (
		<label
			className={`inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 text-[13px] transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-live sm:h-8 ${
				checked ? "border-ink bg-ink font-medium text-ground" : "border-hairline bg-paper text-ink hover:border-line"
			}`}
		>
			<input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
			{children}
		</label>
	);
}
